// Controlled flatpickr-based date input. Wraps `react-flatpickr` so
// every page in the dashboard speaks epoch-ms (matching the BIGINT
// epoch-ms storage convention) without each form re-doing the
// YYYY-MM-DD <-> epoch conversion.
//
// Display format: MM/DD/YYYY (canonical project format). The visible
// alt-input shows MM/DD/YYYY while the real hidden input feeds flatpickr
// an unambiguous Y-m-d value.
//
// Usage:
//   <DateInput value={shiftStartEpoch} onChange={setShiftStartEpoch} />
//   <DateInput value={ts} onChange={setTs} withTime />

import { useMemo } from 'react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

export interface DateInputProps {
  value: number | null;
  onChange: (epochMs: number | null) => void;
  withTime?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  /** Pass through native HTML id for label association. */
  id?: string;
  /** Extra class on the underlying input. */
  className?: string;
  /** Minimum selectable date as epoch ms (e.g. today for due-date fields). */
  minEpoch?: number;
  /** Maximum selectable date as epoch ms. */
  maxEpoch?: number;
}

export function DateInput(props: DateInputProps): JSX.Element {
  const {
    value, onChange, withTime = false, disabled = false,
    placeholder, required = false, id, className,
    minEpoch, maxEpoch,
  } = props;

  // react-flatpickr expects Date | Date[]; null/undefined means "no
  // default". Memoize to avoid recreating Date objects on every render
  // (which the underlying library treats as a value change).
  const flatpickrValue = useMemo(
    () => (value !== null && Number.isFinite(value) ? new Date(value) : undefined),
    [value],
  );

  const options = useMemo(
    () => ({
      dateFormat: withTime ? 'Y-m-d H:i' : 'Y-m-d',
      // Keep an alt-input visible so the user sees MM/DD/YYYY; the
      // hidden real input speaks the unambiguous Y-m-d to flatpickr.
      altInput: true,
      altFormat: withTime ? 'm/d/Y h:i K' : 'm/d/Y',
      enableTime: withTime,
      time_24hr: false,
      allowInput: true,
      disableMobile: true, // always show the styled calendar, not the
                           // native mobile picker
      ...(minEpoch !== undefined ? { minDate: new Date(minEpoch) } : {}),
      ...(maxEpoch !== undefined ? { maxDate: new Date(maxEpoch) } : {}),
    }),
    [withTime, minEpoch, maxEpoch],
  );

  const handleChange = (dates: Date[]): void => {
    if (dates.length === 0) {
      onChange(null);
      return;
    }
    onChange(dates[0]!.getTime());
  };

  return (
    <Flatpickr
      value={flatpickrValue}
      onChange={handleChange}
      options={options}
      disabled={disabled}
      placeholder={placeholder ?? (withTime ? 'mm/dd/yyyy hh:mm' : 'mm/dd/yyyy')}
      required={required}
      id={id}
      className={`form-input date-input ${className ?? ''}`}
    />
  );
}
